import React, { useState, useMemo } from 'react';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  Button,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useAccount } from '../../contexts/Account';
import {
  addNewGoal,
  completeAOBF,
  updateAdvisorOnboardingStep6,
} from '../../services/advisor_services';
import { Enviroments } from '../../services/utils/types';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import AdvisorImageUploader from '../../components/Advisor/Profile/components/advisorImageUploader';
import { uploadToS3 } from '../../services/Amplify/amplify-files-upload';

const styles = () => ({
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  info: {
    fontStyle: 'italic',
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '16px',
  },
  optional: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    marginBottom: '8px',
    marginTop: '16px',
    color: '#646D7A',
  },
  footerText: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    textAlign: 'center',
    marginTop: '12px',
    marginBottom: '36px',
  },
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: '#C7D0DE',
      },
    },
  },
  gridContainerProps: {
    container: true,
    spacing: 0,
    justifyContent: 'center',
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
});

const AOBFForm = () => {
  const { editAccountInformation, accountDetails, tags, currentPlan } =
    useAccount();

  const classes = styles();
  const [firstName, setFirstName] = useState(accountDetails.givenName || '');
  const [lastName, setLastName] = useState(accountDetails.surName || '');
  const [profession, setProfession] = useState(accountDetails.aobfPrimaryExpertise || '');
  const [interestsSelected, setInterestsSelected] = useState([]);
  const [additionalSkills, setAdditionalSkills] = useState(accountDetails.aobfAdditionalExpertise || '');
  const [goal1, setGoal1] = useState('');
  const [goal2, setGoal2] = useState('');
  const [goal3, setGoal3] = useState('');
  const [avatarImage, setAvatarImage] = useState();
  const [originalImage, setOriginalImage] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const interestsOptions = [
    'Board Seats',
    'Consulting',
    'Mentoring',
    'Networking',
    'New Ventures',
    'Full-Time Roles',
  ];

  const professionOptions = [
    'Founder or CEO',
    'Marketing',
    'Business Professional',
    'Operations',
    'Technology',
    'Product',
    'Finance',
    'Sales',
    'Human Resources',
    'Legal',
    'Other',
  ];

  const disableButton = () => {
    if (!firstName || !lastName || isLoading || !profession) {
      return true;
    }
    if (interestsSelected.length === 0) {
      return true;
    }
    return false;
  };

  const removeAllInterests = () => {
    setInterestsSelected([]);
  };

  function setImageFiles(images) {
    setAvatarImage(images.image);
    setOriginalImage(images.originalImage);
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      if (goal1.trim()) {
        await addNewGoal({ text: goal1.trim(), advisorId: accountDetails.id });
      }
      if (goal2.trim()) {
        await addNewGoal({ text: goal2.trim(), advisorId: accountDetails.id });
      }
      if (goal3.trim()) {
        await addNewGoal({ text: goal3.trim(), advisorId: accountDetails.id });
      }
      let uploadedAvatarId = null;
      let uploadedOriginalId = null;
      let bucketImages = process.env.REACT_APP_S3_BUCKET_IMAGES;
      if (process.env.NODE_ENV === Enviroments.PRODUCTION) {
        bucketImages = process.env.REACT_APP_S3_BUCKET_IMAGES.replace(
          'us-west-2',
          'us-east-1'
        );
      }
      if (avatarImage) {
        const imageId = `${uuidv4()}.${avatarImage.type.split('/')[1]}`;
        await uploadToS3({
          BLOB_LOAD: avatarImage,
          CONTENT_TYPE: avatarImage.type,
          FILE_ID: imageId,
          S3_BUCKET: bucketImages,
        });
        uploadedAvatarId = `public/${imageId}`;
      }
      if (originalImage) {
        const imageId = `${uuidv4()}.${originalImage.type.split('/')[1]}`;
        await uploadToS3({
          BLOB_LOAD: originalImage,
          CONTENT_TYPE: originalImage.type,
          FILE_ID: imageId,
          S3_BUCKET: process.env.REACT_APP_S3_BUCKET_ORIGINAL_IMAGES,
        });
        uploadedOriginalId = `public/${imageId}`;
      }
      // Complete AOBF
      await completeAOBF({
        id: accountDetails.id,
        givenName: firstName,
        surName: lastName,
        interests: interestsSelected,
        skillTagIds: [],
        aobfAdditionalExpertise: additionalSkills,
        aobfPrimaryExpertise: profession,
        avatarImageId: uploadedAvatarId || "",
        originalImageId: uploadedOriginalId || "",
      });
      accountDetails.onboardingStatus = 'Complete';
      editAccountInformation({
        onboardingStatus: 'Complete',
        givenName: firstName,
        surName: lastName,
      });
      await updateAdvisorOnboardingStep6({
        ADVISOR_ID: accountDetails.id,
        ONBOARDING_STATUS: 'Complete',
      });
      window.location.replace(currentPlan.onboardingRedirectUrl);
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
      console.error(error);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Box
          sx={{
            mx: 'auto',
          }}
          id="aobf-image-uploader"
        >
          <AdvisorImageUploader resultFiles={setImageFiles} />
        </Box>
      </Box>
      <Grid {...classes.gridContainerProps}>
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>Your name:</Typography>
        </Grid>
      </Grid>
      <Grid {...classes.gridContainerProps}>
        <Grid item xxs={12} md={4}>
          <TextField
            fullWidth
            placeholder="First"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            sx={{ ...classes.textField, paddingRight: { xxs: 0, md: '6px' } }}
            id="aobf-first-name"
          />
        </Grid>
        <Grid item xxs={12} md={4}>
          <TextField
            fullWidth
            placeholder="Last"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            sx={{ ...classes.textField, paddingLeft: { xxs: 0, md: '6px' } }}
            id="aobf-last-name"
          />
        </Grid>
      </Grid>
      <Grid {...classes.gridContainerProps}>
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>Primary Area of Expertise:</Typography>
        </Grid>
        <Grid {...classes.gridItemProps}>
          <Select
            fullWidth
            value={profession}
            onChange={(e) => setProfession(e.target.value)}
            displayEmpty
            sx={{ marginBottom: '8px', borderRadius: '10px' }}
            id="aobf-profession"
          >
            <MenuItem value="" disabled>
              Select one
            </MenuItem>
            {professionOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid {...classes.gridContainerProps}>
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>
            Please add up to 3 additional skills, industries or targeted areas
            of expertise (optional):
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps}>
          <TextField
            fullWidth
            placeholder="ex. Software, AI, B2B, 50-Something Demographic"
            value={additionalSkills}
            onChange={(e) => setAdditionalSkills(e.target.value)}
            inputProps={{ maxLength: 200 }}
            sx={classes.textField}
            id="aobf-additional-skills"
          />
        </Grid>
      </Grid>
      <Grid {...classes.gridContainerProps}>
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.label}>Your interests:</Typography>
          <Typography sx={classes.info}>
            Select the types of professional opportunities you're most
            interested in.
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={interestsOptions}
            getOptionLabel={(option) => option}
            value={interestsSelected}
            onChange={(event, newValue) => setInterestsSelected(newValue)}
            id="aobf-interests"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  interestsSelected.length ? '' : 'Select all that apply'
                }
                sx={classes.textField}
              />
            )}
            renderTags={() =>
              interestsSelected.length > 0 ? (
                <Chip
                  label={`(${interestsSelected.length}) selected`}
                  onDelete={removeAllInterests}
                  sx={{ margin: 0, padding: 0, backgroundColor: '#F5F7FA' }}
                />
              ) : null
            }
          />
        </Grid>
        <Grid {...classes.gridContainerProps}>
          <Grid {...classes.gridItemProps}>
            <Typography sx={classes.label}>Your Goals (optional):</Typography>
          </Grid>
          <Grid {...classes.gridItemProps}>
            <TextField
              fullWidth
              placeholder="Become a CXO / Increase My Income 50%"
              value={goal1}
              onChange={(e) => setGoal1(e.target.value)}
              multiline
              minRows={1}
              sx={classes.textField}
              id='aobf-goal1'
            />
          </Grid>
          <Grid {...classes.gridItemProps}>
            <TextField
              fullWidth
              placeholder="Start My Own Business/Non-Profit/Special Project"
              value={goal2}
              onChange={(e) => setGoal2(e.target.value)}
              multiline
              minRows={1}
              sx={classes.textField}
              id='aobf-goal2'
            />
          </Grid>
          <Grid {...classes.gridItemProps}>
            <TextField
              fullWidth
              placeholder="Someday Work Less & Just Do Advisory Work"
              value={goal3}
              onChange={(e) => setGoal3(e.target.value)}
              multiline
              minRows={1}
              sx={classes.textField}
              id='aobf-goal3'
            />
          </Grid>
        </Grid>
        <Grid {...classes.gridItemProps}>
          <Typography sx={classes.footerText}>
            When you click View Dashboard, you'll see a pop-up to take a 60
            second tour of your Advisor Dashboard, which is a great way to see
            everything you can do!
          </Typography>
        </Grid>
        <Grid {...classes.gridItemProps} md={5}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={disableButton()}
            id="aobf-submit-button"
            sx={{
              borderRadius: '50px',
              backgroundColor: '#3171F6',
              '&:hover': { backgroundColor: '#3171F6' },
              height: '48px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 700,
            }}
          >
            {isLoading ? <LoadingLogoSkeleton /> : 'View Dashboard'}
          </Button>
        </Grid>
      </Grid>
      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Something went wrong"
      />
    </Box>
  );
};

export default AOBFForm;
